import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpRightFromSquare, faMagic, faCheckCircle, faChevronRight, faFileAlt, faPencilAlt, faExclamationTriangle, faSparkles } from '@fortawesome/free-solid-svg-icons';
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import { formatToText } from 'utils/jsonhandlers/jsontohtml';
import useModalStore from 'zustand/zustandstore';
import api from 'api/api';
import agapi from 'api/agapi';
import { VisitSummaryModal, LabsMiniModal, ImagingModal, DocumentModal } from './modals/timelinemodals';
import './careiq.css';

/* ─── Design Tokens (matching CareIQTab's coral brand palette) ─── */
const BRAND = {
  coral: "#E8653A", coralLight: "#FFF3EF", coralMid: "#F4A68A",
  navy: "#1B2A4A", navyLight: "#2D4166",
  slate: "#64748B", slateMuted: "#94A3B8",
  emerald: "#10B981", emeraldLight: "#D1FAE5",
  amber: "#F59E0B", amberLight: "#FEF3C7",
  red: "#EF4444", redLight: "#FEE2E2",
  purple: "#8B5CF6", purpleLight: "#EDE9FE",
  blue: "#3B82F6", blueLight: "#DBEAFE",
  surface: "#FFFFFF", surfaceMuted: "#F8FAFC",
  border: "#E2E8F0", borderLight: "#F1F5F9",
};

const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/* ─── Timeline Helpers (matching CareIQTab) ─── */
const pillClass = (type) => {
  if (type === "visit") return "visit";
  if (type === "lab") return "lab";
  if (type === "imaging") return "imaging";
  return "visit";
};

const pillLabel = (type) => {
  if (type === "visit") return "Visit";
  if (type === "lab") return "Lab";
  if (type === "imaging") return "Imaging";
  return type;
};

function isOutOfRange(value, refRange) {
  if (!value || !refRange) return false;
  const numericValue = parseFloat(value);
  if (isNaN(numericValue)) return false;
  const cleaned = refRange.replace(/\s/g, "");
  if (cleaned.includes("-")) {
    const [min, max] = cleaned.split("-").map(Number);
    if (isNaN(min) || isNaN(max)) return false;
    return numericValue < min || numericValue > max;
  }
  if (cleaned.startsWith("<=")) return numericValue > Number(cleaned.replace("<=", ""));
  if (cleaned.startsWith(">=")) return numericValue < Number(cleaned.replace(">=", ""));
  return false;
}

const DxPill = ({ label, code, onAction, actionLabel }) => (
  <div style={{
    display: "flex", alignItems: "center", gap: "5px", padding: "3px 8px",
    background: "#fff", border: `1px solid ${BRAND.border}`, borderRadius: "6px",
    boxShadow: "0 1px 2px rgba(0,0,0,0.03)", transition: "all 0.15s ease",
  }}>
    <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
      <span style={{ fontSize: "11px", fontWeight: 600, color: BRAND.navy, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{label}</span>
      {code && <span style={{ fontSize: "9px", color: BRAND.slateMuted, fontWeight: 700 }}>{code}</span>}
    </div>
    {onAction && (
      <button onClick={onAction} style={{
        background: BRAND.coral, color: "#fff", border: "none", borderRadius: "4px",
        width: "16px", height: "16px", display: "flex", alignItems: "center", justifyContent: "center",
        cursor: "pointer", fontSize: "12px", fontWeight: "bold"
      }}>{actionLabel}</button>
    )}
  </div>
);

const CareIQView = ({
  jsonData = [],
  summaryjson = {},
  fields = {},
  setSectionData,
  setFields,
  setPopUp,
  setSelectedCard,
  setIsShareModal,
  timelinedata = {},
  imgdata = [],
  isTimeLineLoading = false,
  data = {},
}) => {
  const mydata = useModalStore();
  const sidebarRef = useRef(null);
  const [sidebarH, setSidebarH] = useState(null);
  
  // -- Modals --
  const [visitModal, setVisitModal] = useState(null);
  const [labModal, setLabModal] = useState(null);
  const [imagingModal, setImagingModal] = useState(null);
  const [docModal, setDocModal] = useState(null);

  // -- CareIQ State --
  const [careIQ, setCareIQ] = useState(null);
  const [currentOrders, setCurrentOrders] = useState([]);
  const [editingOrderIdx, setEditingOrderIdx] = useState(null);
  const [editingOrderText, setEditingOrderText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [insightsTab, setInsightsTab] = useState("gaps");
  const [timelineSearch, setTimelineSearch] = useState("");
  const [timelineExpanded, setTimelineExpanded] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [encounters, setEncounters] = useState([]);
  const [progressNotes, setProgressNotes] = useState([]);

  const patientId = data?.config?.patientid;
  const practiceId = data?.config?.practiceid;

  /* ── Fetch CareIQ Intelligence Bundle ── */
  useEffect(() => {
    if (!patientId || !practiceId) return;
    let cancelled = false;
    const fetchAll = async () => {
      setIsLoading(true);
      setFetchError(null);
      try {
        const results = await Promise.allSettled([
          // 0: CareIQ insights
          agapi.get(`/v1/kg/${practiceId}/todo/${patientId}`),
          // 1: Labs
          agapi.get(`/v1/${practiceId}/labs/patient/${patientId}/get`),
          // 2: Imaging documents
          agapi.get(`/v1/fhir-mirror/${practiceId}/patients/${patientId}/documents/imagingresult`),
          // 3: FHIR Encounters (richer visit data)
          agapi.post(`/v1/fhir-mirror/${practiceId}/patient/${patientId}/encounter`, { departmentid: 1, limit: 20 }),
          // 4: Progress Notes (Handwritten/Encounter documents)
          agapi.post(`/v1/fhir-mirror/${practiceId}/patient/${patientId}/procedures/get`, {
            category: 'ProgressNotes',
            department_id: '1',
          }),
          // 5: Current orders
          agapi.post(`/v1/${practiceId}/orders/get`, {
            patientid: patientId,
            orderdate: new Date().toISOString().split('T')[0]
          }),
        ]);

        if (cancelled) return;

        const rejected = results.filter(r => r.status === "rejected");
        if (rejected.length > 0) {
          console.warn("[CareIQView] Some fetch calls failed:", rejected.map(r => r.reason?.message));
        }

        if (results[0].status === "fulfilled") {
          const d = results[0].value?.data || results[0].value;
          setCareIQ(d?.patient_insights || d);
        }
        if (results[2].status === "fulfilled") {
          const raw = results[2].value?.data || results[2].value;
          const docs = Array.isArray(raw) ? raw : (raw?.imagingresults || raw?.data || []);
          console.log("[CareIQView] Imaging documents loaded:", docs.length, "items");
          setDocuments(Array.isArray(docs) ? docs : []);
        } else {
          console.warn("[CareIQView] Imaging documents fetch failed:", results[2].reason?.message);
        }
        if (results[3].status === "fulfilled") {
          const raw = results[3].value?.data || results[3].value;
          setEncounters(raw?.encounters || []);
        }
        if (results[4].status === "fulfilled") {
          const raw = results[4].value?.data || results[4].value;
          const notes = raw?.encounterdocuments || raw?.data?.encounterdocuments || [];
          setProgressNotes(notes);
        }
        if (results[5].status === "fulfilled") {
          const d = results[5].value?.data || results[5].value;
          setCurrentOrders(Array.isArray(d) ? d : (d?.orders || d?.data || []));
        }
      } catch (err) {
        console.error("[CareIQView] fetchAll crash:", err);
        if (!cancelled) setFetchError("An unexpected error occurred while loading clinical data.");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchAll();
    return () => { cancelled = true; };
  }, [patientId, practiceId]);

  const [aiDiagnoses, setAiDiagnoses] = useState([]);

  /* ── Fetch Persisted Diagnoses ── */
  useEffect(() => {
    if (!patientId) return;
    let cancelled = false;
    const fetchDx = async () => {
      try {
        const today = new Date().toISOString().slice(0, 10);
        const res = await api.post(
          `/v1/patient/${patientId}/recording/details`,
          { filter_array: { date_filter: { start_date: today, end_date: today } } }
        );
        if (cancelled) return;
        const records = res?.data || res;
        if (Array.isArray(records) && records.length > 0) {
          const latest = records.sort((a, b) => new Date(b.ts) - new Date(a.ts))[0];
          const nj = latest?.note_json;
          const savedDx = nj?.suggested_icd10 || [];
          if (savedDx.length > 0) {
            setAiDiagnoses(savedDx.map(d => ({ icd10_code: d.icd10_code || d.icd10 || "", description: d.description })));
          }
        }
      } catch (e) {
        console.warn("[CareIQView] Failed to fetch persisted Dx:", e?.message);
      }
    };
    fetchDx();
    return () => { cancelled = true; };
  }, [patientId]);

  /* ── Derived Data ── */
  const ciq = careIQ || {};
  const allAiDiagnoses = useMemo(() => {
    const fromKg = ciq?.care_intelligence?.suggested_icd10 || [];
    const seen = new Set(aiDiagnoses.map(d => d.description?.toLowerCase()));
    const merged = [...aiDiagnoses];
    fromKg.forEach(d => {
      if (!seen.has(d.description?.toLowerCase())) {
        merged.push(d);
      }
    });
    return merged;
  }, [ciq, aiDiagnoses]);

  const suggestedOrders = ciq?.care_intelligence?.suggested_orders || [];
  const careGaps = ciq?.care_intelligence?.care_gaps || [];
  const suggestions = ciq?.care_intelligence?.suggestions || [];
  const riskFactors = ciq?.risk_radar?.risk_factors || [];
  const followUps = ciq?.followup_central?.items || [];
  const criticalAlerts = useMemo(() => {
    const rows = ciq?.["critical_alerts/clinical_intel"]?.data_rows || [];
    return rows.filter(r => ["abnormal", "high", "overweight", "low"].includes((r.status || "").toLowerCase()));
  }, [ciq]);

  /* Sidebar height logic removed in favor of CSS sticky behavior */

  /* ── Timeline Builder ── */
  const timelineEvents = useMemo(() => {
    const events = [];

    // --- Visit/Lab events from timeline data ---
    const visitDates = new Set();
    if (timelinedata) {
      Object.entries(timelinedata).forEach(([dateStr, details]) => {
        const [year, month, day] = dateStr.split('-');
        const d = new Date(year, parseInt(month) - 1, day);
        visitDates.add(dateStr);
        events.push({
          date: d, dateStr: `${monthNames[+month - 1]} ${+day}`, year: parseInt(year),
          label: details?.title || (details?.labs ? "Labs" : "Visit"),
          type: details?.labs ? "lab" : "visit",
          dotColor: details?.labs ? "pending" : "normal",
          raw: { date: dateStr, details }
        });
      });
    }

    // --- FHIR Encounters (to catch visits not in timelinedata) ---
    encounters.forEach(enc => {
      const dateRaw = enc.startdate || enc.date || enc.encounterdate;
      if (!dateRaw) return;
      const d = new Date(dateRaw);
      const dateStr = dateRaw.split('T')[0];
      if (visitDates.has(dateStr)) return; // Already added from timelinedata

      events.push({
        date: d,
        dateStr: `${monthNames[d.getMonth()]} ${d.getDate()}`,
        year: d.getFullYear(),
        label: enc.appointmenttypename || "Office Visit",
        type: "visit",
        dotColor: "normal",
        raw: { date: dateStr, ...enc }
      });
    });

    // --- Imaging events from /documents/imagingresult ---
    documents.forEach(doc => {
      const dateRaw = doc.createddate || doc.createddatetime;
      if (!dateRaw) return;
      const d = new Date(dateRaw);
      if (isNaN(d.getTime())) return;
      events.push({
        date: d,
        dateStr: `${monthNames[d.getMonth()]} ${d.getDate()}`,
        year: d.getFullYear(),
        label: doc.documentdescription || doc.description || doc.label || "Imaging",
        type: "imaging",
        dotColor: "pending",
        raw: { ...doc, patientid: patientId },
      });
    });

    // --- Progress Notes (Athena encounter documents) ---
    progressNotes.forEach(note => {
      const dateRaw = note.createddate || note.createddatetime;
      if (!dateRaw) return;
      const d = new Date(dateRaw);
      events.push({
        date: d,
        dateStr: `${monthNames[d.getMonth()]} ${d.getDate()}`,
        year: d.getFullYear(),
        label: note.documentdescription || note.description || "Progress Note",
        type: "document",
        dotColor: "pending",
        raw: { ...note, patientid: patientId }
      });
    });

    // --- Legacy imgdata (BlinkScribe documents) ---
    if (imgdata) {
      imgdata.forEach(item => {
        if (!item.date) return;
        const [year, month, day] = item.date.split('-');
        const d = new Date(year, parseInt(month) - 1, day);
        events.push({
          date: d, dateStr: `${monthNames[+month - 1]} ${+day}`, year: parseInt(year),
          label: item.title || "Document", type: "document", dotColor: "pending", raw: item
        });
      });
    }

    events.sort((a, b) => b.date.getTime() - a.date.getTime());
    return events;
  }, [timelinedata, imgdata, documents, encounters, progressNotes, patientId]);

  /* ── Filtered timeline ── */
  const filteredTimeline = useMemo(() => {
    const q = timelineSearch.trim().toLowerCase();
    if (!q) return timelineEvents;
    return timelineEvents.filter(ev => 
        ev.label.toLowerCase().includes(q) || 
        ev.dateStr.toLowerCase().includes(q) || 
        ev.type.toLowerCase().includes(q)
    );
  }, [timelineEvents, timelineSearch]);

  const timelineGroups = useMemo(() => {
    const groups = [];
    let lastYear = null;
    filteredTimeline.forEach(ev => {
      const yr = ev.year;
      if (yr !== lastYear) { groups.push({ year: yr, events: [] }); lastYear = yr; }
      groups[groups.length - 1].events.push(ev);
    });
    return groups;
  }, [filteredTimeline]);

  const handleTimelineClick = (ev) => {
    console.log("[CareIQView] ▶️ Timeline clicked:", ev.type, ev.label, "| raw:", ev.raw);
    if (!ev.raw) return;
    
    // Clear all modals first to prevent overlapping state/scroll issues
    setVisitModal(null);
    setLabModal(null);
    setImagingModal(null);
    setDocModal(null);

    const details = ev.raw?.details;

    switch (ev.type) {
      case "document": 
        setDocModal(ev.raw); 
        break;
      case "lab": 
        setLabModal(ev.raw); 
        break;
      case "imaging": 
        setImagingModal(ev.raw); 
        break;
      case "visit":
        setVisitModal(ev.raw);
        break;
      default:
        if (details?.encounter_summary) setVisitModal(ev.raw);
        else if (details?.labs) setLabModal(ev.raw);
        else if (details?.images) setImagingModal(ev.raw);
        break;
    }
  };
  
  const handleAddOrder = (order) => {
    const newOrder = {
      ordername: order.order || order.description || order.recommendation,
      status: "Added",
      addedLocally: true
    };
    setCurrentOrders(prev => [newOrder, ...prev]);
  };

  const startEditOrder = (idx, order) => {
    setEditingOrderIdx(idx);
    setEditingOrderText(order.ordername || order.description || "");
  };

  const saveEditOrder = () => {
    if (editingOrderIdx !== null) {
      setCurrentOrders(prev => prev.map((o, i) => i === editingOrderIdx ? { ...o, ordername: editingOrderText } : o));
      setEditingOrderIdx(null);
    }
  };

  const activeSections = useMemo(() => {
    return jsonData.map((section, idx) => {
      const sectionKey = section.placeholder?.toLowerCase();
      const summaryValue = summaryjson?.[sectionKey] || '';
      const fieldValue = fields?.[section.key] || '';
      const displayValue = summaryValue || (section.value ? formatToText(section.value) : fieldValue);
      const hasContent = displayValue && (typeof displayValue === 'string' ? displayValue.trim().length > 0 : true);
      return {
        ...section, idx,
        displayValue: typeof displayValue === 'string' ? displayValue : String(displayValue || ''),
        hasContent,
        isRosOrPe: ['review of systems', 'physical examination'].includes(section.label),
        isNormal: summaryValue && typeof summaryValue === 'string' ? summaryValue.replace(/\s|\./g, '') === 'Normal' : false,
      };
    }).filter(s => s.hasContent);
  }, [jsonData, summaryjson, fields]);

  return (
    <div className="ciqt-root">
      {fetchError && (
        <div style={{ background: "#FEE2E2", color: "#B91C1C", padding: "12px", borderRadius: "8px", marginBottom: "15px", fontSize: "13px", fontWeight: 600, display: "flex", alignItems: "center", gap: "10px" }}>
          <FontAwesomeIcon icon={faExclamationTriangle} />
          {fetchError}
          <button onClick={() => window.location.reload()} style={{ marginLeft: "auto", background: "#B91C1C", color: "white", border: "none", borderRadius: "4px", padding: "4px 10px", cursor: "pointer", fontSize: "11px" }}>Retry</button>
        </div>
      )}
      <div className="ciqt-layout">
        <div className="ciqt-main">
          
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "10px", marginBottom: "15px" }}>
            {activeSections.map((section, idx) => (
              <div key={idx} style={{
                background: BRAND.surface, borderRadius: "12px", border: `1px solid ${BRAND.border}`,
                boxShadow: "0 1px 3px rgba(0,0,0,0.04)", overflow: "hidden", display: "flex", flexDirection: "column",
                gridColumn: (idx % 7 === 3 || idx % 7 === 6) ? "span 3" : (idx % 7 === 5) ? "span 2" : "span 1",
                cursor: "pointer"
              }} onClick={() => { setSectionData(section); setFields(prev => ({ ...prev, [section.key]: formatToText(section.value) })); setPopUp(true); }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 14px", background: `linear-gradient(135deg, ${BRAND.surfaceMuted}, #F0F4FF)`, borderBottom: `1px solid ${BRAND.borderLight}` }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <div style={{ width: "6px", height: "6px", borderRadius: "50%", background: section.isRosOrPe ? (section.isNormal ? BRAND.emerald : BRAND.red) : BRAND.blue }} />
                    <span style={{ fontSize: "12px", fontWeight: 700, color: BRAND.navy }}>{section.label?.toUpperCase()}</span>
                  </div>
                  <FontAwesomeIcon icon={faUpRightFromSquare} style={{ fontSize: "11px", color: BRAND.slateMuted }} />
                </div>
                <div style={{ padding: "10px 14px", fontSize: "12px", color: BRAND.slate, lineHeight: 1.6, maxHeight: "140px", overflow: "hidden" }}>
                  <div dangerouslySetInnerHTML={{ __html: section.displayValue }} />
                </div>
              </div>
            ))}
          </div>

          <div style={{ background: "#EDF1F7", borderRadius: "12px", padding: "10px", border: `1px solid ${BRAND.border}`, marginBottom: "12px" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: "0", alignItems: "stretch" }}>
              <div style={{ background: "#fff", borderRadius: "10px", border: `1px solid ${BRAND.border}`, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                <div style={{ padding: "8px 12px", background: `linear-gradient(135deg, #FFF7ED, ${BRAND.coralLight})`, borderBottom: `1px solid ${BRAND.borderLight}` }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <span style={{ fontSize: "11px", fontWeight: 700, color: BRAND.navy }}>SUGGESTED</span>
                  </div>
                </div>
                <div style={{ padding: "10px", flex: 1, overflowY: "auto", maxHeight: "250px" }}>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", marginBottom: "8px" }}>
                    {allAiDiagnoses.map((dx, i) => <DxPill key={i} label={dx.description} code={dx.icd10_code} />)}
                  </div>
                  {suggestedOrders.map((o, i) => (
                    <div key={i} style={{ padding: "8px 0", borderTop: `1px solid ${BRAND.borderLight}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: "12px", color: BRAND.navy }}>{o.order || o.description}</span>
                      <button 
                        onClick={() => handleAddOrder(o)}
                        style={{ background: BRAND.emerald, color: "#fff", border: "none", borderRadius: "4px", padding: "2px 8px", fontSize: "11px", cursor: "pointer" }}
                      >
                        Add
                      </button>
                    </div>
                  ))}
                  {(allAiDiagnoses.length === 0 && suggestedOrders.length === 0) && <div className="ciqt-empty">No suggestions</div>}
                </div>
              </div>

              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "0 8px" }}>
                <div style={{ width: "1px", height: "30px", background: `repeating-linear-gradient(to bottom, ${BRAND.border} 0px, ${BRAND.border} 4px, transparent 4px, transparent 8px)` }} />
                <FontAwesomeIcon icon={faChevronRight} style={{ color: BRAND.slateMuted, margin: "4px 0" }} />
                <FontAwesomeIcon icon={faChevronRight} style={{ color: BRAND.coralMid, margin: "4px 0" }} />
                <div style={{ width: "1px", height: "30px", background: `repeating-linear-gradient(to bottom, ${BRAND.border} 0px, ${BRAND.border} 4px, transparent 4px, transparent 8px)` }} />
              </div>

              <div style={{ background: "#fff", borderRadius: "10px", border: `1px solid ${BRAND.border}`, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                <div style={{ padding: "8px 12px", background: "linear-gradient(135deg, #EEF4FF, #E8F0FE)", borderBottom: `1px solid ${BRAND.borderLight}` }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <FontAwesomeIcon icon={faFileAlt} style={{ color: BRAND.blue, fontSize: "12px" }} />
                    <span style={{ fontSize: "11px", fontWeight: 700, color: BRAND.navy }}>CURRENT ORDERS</span>
                  </div>
                </div>
                <div style={{ padding: "10px", flex: 1, overflowY: "auto", maxHeight: "250px" }}>
                  {currentOrders.map((o, i) => (
                    <div key={i} style={{ padding: "8px 0", borderBottom: `1px solid ${BRAND.borderLight}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div style={{ flex: 1 }}>
                        {editingOrderIdx === i ? (
                          <div style={{ display: "flex", gap: "4px" }}>
                            <input 
                              autoFocus
                              value={editingOrderText} 
                              onChange={(e) => setEditingOrderText(e.target.value)}
                              onBlur={saveEditOrder}
                              onKeyDown={(e) => e.key === 'Enter' && saveEditOrder()}
                              style={{ fontSize: "12px", width: "100%", padding: "2px 4px", border: `1px solid ${BRAND.coral}` }}
                            />
                          </div>
                        ) : (
                          <>
                            <div style={{ fontSize: "12px", fontWeight: 600, color: BRAND.navy }}>{o.ordername || o.description}</div>
                            <div style={{ fontSize: "10px", color: BRAND.slateMuted }}>{o.status}</div>
                          </>
                        )}
                      </div>
                      <FontAwesomeIcon 
                        icon={faPencilAlt} 
                        onClick={() => startEditOrder(i, o)}
                        style={{ fontSize: "10px", color: BRAND.slateMuted, cursor: "pointer", marginLeft: "8px" }} 
                      />
                    </div>
                  ))}
                  {currentOrders.length === 0 && <div className="ciqt-empty">No active orders</div>}
                </div>
              </div>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1.5fr", gap: "10px" }}>
            <div style={{ background: "#fff", borderRadius: "12px", border: `1px solid ${BRAND.border}`, overflow: "hidden" }}>
              <div style={{ padding: "7px 10px", background: `linear-gradient(135deg, ${BRAND.purpleLight}, #F5F3FF)`, borderBottom: `1px solid ${BRAND.border}`, display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontSize: "10px", fontWeight: 700, color: BRAND.navy }}>FOLLOW-UPS</span>
                <span style={{ background: BRAND.purple, color: "#fff", fontSize: "10px", borderRadius: "10px", padding: "0 6px" }}>{followUps.length}</span>
              </div>
              <div style={{ padding: "10px", maxHeight: "200px", overflowY: "auto" }}>
                {followUps.map((f, i) => (
                  <div key={i} style={{ display: "flex", gap: "8px", padding: "6px 0", borderBottom: `1px solid ${BRAND.borderLight}` }}>
                    <div style={{ width: "5px", height: "5px", borderRadius: "50%", background: BRAND.amber, marginTop: "5px" }} />
                    <span style={{ fontSize: "11px", color: BRAND.navy }}>{f.item || f.description}</span>
                  </div>
                ))}
                {followUps.length === 0 && <div className="ciqt-empty">No follow-ups</div>}
              </div>
            </div>

            <div style={{ background: "#fff", borderRadius: "12px", border: `1px solid ${BRAND.border}`, overflow: "hidden" }}>
              <div style={{ padding: "7px 10px", background: `linear-gradient(135deg, ${BRAND.redLight}, #FFF5F5)`, borderBottom: `1px solid ${BRAND.border}`, display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontSize: "10px", fontWeight: 700, color: BRAND.navy }}>CRITICAL VALUES</span>
                <span style={{ background: BRAND.red, color: "#fff", fontSize: "10px", borderRadius: "10px", padding: "0 6px" }}>{criticalAlerts.length}</span>
              </div>
              <div style={{ padding: "10px", maxHeight: "200px", overflowY: "auto" }}>
                {criticalAlerts.map((r, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: `1px solid ${BRAND.borderLight}` }}>
                    <span style={{ fontSize: "11px", color: BRAND.navy }}>{r.label}</span>
                    <span style={{ fontSize: "11px", fontWeight: 700, color: BRAND.red }}>{r.value}</span>
                  </div>
                ))}
                {criticalAlerts.length === 0 && <div className="ciqt-empty">No critical alerts</div>}
              </div>
            </div>

            <div style={{ background: "#fff", borderRadius: "12px", border: `1px solid ${BRAND.border}`, overflow: "hidden" }}>
              <div style={{ display: "flex", borderBottom: `1px solid ${BRAND.borderLight}` }}>
                {["gaps", "recs", "risks"].map(t => (
                  <button key={t} onClick={() => setInsightsTab(t)} style={{
                    flex: 1, padding: "8px", background: "transparent", border: "none", cursor: "pointer",
                    borderBottom: insightsTab === t ? `2px solid ${BRAND.coral}` : "none",
                    fontSize: "10px", fontWeight: 700, color: insightsTab === t ? BRAND.coral : BRAND.slateMuted
                  }}>{t.toUpperCase()}</button>
                ))}
              </div>
              <div style={{ padding: "10px", maxHeight: "165px", overflowY: "auto" }}>
                {insightsTab === "gaps" && careGaps.map((g, i) => <div key={i} style={{ fontSize: "11px", padding: "6px 0", borderBottom: `1px solid ${BRAND.borderLight}`, borderLeft: `3px solid ${BRAND.amber}`, paddingLeft: "8px" }} dangerouslySetInnerHTML={{ __html: g.text || g.description }} />)}
                {insightsTab === "recs" && suggestions.map((s, i) => <div key={i} style={{ fontSize: "11px", padding: "6px 0", borderBottom: `1px solid ${BRAND.borderLight}`, borderLeft: `3px solid ${BRAND.coral}`, paddingLeft: "8px" }} dangerouslySetInnerHTML={{ __html: s.recommendation || s.description }} />)}
                {insightsTab === "risks" && riskFactors.map((r, i) => <div key={i} style={{ fontSize: "11px", padding: "6px 0", borderBottom: `1px solid ${BRAND.borderLight}`, borderLeft: `3px solid ${BRAND.blue}`, paddingLeft: "8px" }} dangerouslySetInnerHTML={{ __html: r.risk || r.description }} />)}
                {(insightsTab === "gaps" && careGaps.length === 0) && <div className="ciqt-empty">No care gaps</div>}
              </div>
            </div>
          </div>
        </div>

        {/* ── RIGHT: Sidebar Timeline ── */}
        <div className="ciqt-sidebar">
          <div className="ciqt-tl-panel">
            <div className="ciqt-tl-header" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                🕐 Patient Timeline
                {(isTimeLineLoading || isLoading) && (
                    <span style={{ display: "inline-block", width: 12, height: 12, border: "2px solid #E5E7EB", borderTop: "2px solid #E17954", borderRadius: "50%", animation: "ciqt-spin 0.7s linear infinite" }} />
                )}
            </div>
            <div className="ciqt-tl-search">
                <input type="text" placeholder="Search timeline… (e.g. A1C, CBC)" value={timelineSearch} onChange={(e) => setTimelineSearch(e.target.value)} />
            </div>
            <div className="ciqt-tl-scroll">
              {filteredTimeline.length === 0 ? (
                  <div className="ciqt-empty">No events found</div>
              ) : (
                timelineGroups.map((g) => (
                  <div key={g.year} className="ciqt-tl-year-group">
                      <div className="ciqt-tl-year-header">{g.year}</div>
                      {g.events.map((ev, i) => {
                          const q = timelineSearch.trim().toLowerCase();
                          let matchedTests = null;
                          if (q && ev.type === "lab" && ev.raw?.details?.labs) {
                              const labs = ev.raw.details.labs;
                              const allTests = Array.isArray(labs) ? labs : (labs.tests || labs.data || []);
                              const hits = allTests.filter(t => (t.testname || t.name || "").toLowerCase().includes(q) || (t.panelname || "").toLowerCase().includes(q));
                              if (hits.length > 0) {
                                  matchedTests = hits.map(t => ({
                                      name: t.testname || t.name || "",
                                      val: t.value || t.latestResult?.value || "—",
                                      units: t.units || t.latestResult?.units || "",
                                      refrange: t.refrange || t.latestResult?.refrange || "",
                                      abnormal: isOutOfRange(t.value || t.latestResult?.value, t.refrange || t.latestResult?.refrange)
                                  }));
                              }
                          }
                          return (
                              <div key={i} style={{ cursor: "pointer" }} onClick={() => handleTimelineClick(ev)}>
                                  <div className="ciqt-tl-row">
                                      <div className={`ciqt-tl-dot ciqt-dot-${ev.dotColor}`} />
                                      <span className="ciqt-tl-date">{ev.dateStr}</span>
                                      <span className="ciqt-tl-label-text" title={ev.label}>{ev.label}</span>
                                      <span className={`ciqt-tl-pill ciqt-pill-${pillClass(ev.type)}`}>{pillLabel(ev.type)}</span>
                                  </div>
                                  {matchedTests && (
                                      <div style={{ marginLeft: "18px", marginBottom: "6px", borderLeft: "2px solid #c2d0e2", paddingLeft: "10px" }}>
                                          {matchedTests.map((mt, j) => (
                                              <div key={j} style={{ display: "flex", alignItems: "baseline", flexWrap: "wrap", gap: "4px 6px", padding: "3px 0", fontSize: "11px", lineHeight: 1.5 }}>
                                                  <span style={{ fontWeight: 600, color: mt.abnormal ? "#DC2626" : "#374151" }}>{mt.name}</span>
                                                  <span style={{ fontWeight: 700, fontSize: "12px", color: mt.abnormal ? "#DC2626" : "#111827" }}>{mt.val}</span>
                                                  {mt.units && <span style={{ fontSize: "10px", color: "#9CA3AF" }}>{mt.units}</span>}
                                                  {mt.abnormal && <span style={{ fontSize: "8px", background: "#DC2626", color: "#fff", borderRadius: "3px", padding: "1px 4px", fontWeight: 700 }}>⚠</span>}
                                              </div>
                                          ))}
                                      </div>
                                  )}
                              </div>
                          );
                      })}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Modals ── */}
      {visitModal && <VisitSummaryModal eventData={visitModal} patientId={patientId} onClose={() => setVisitModal(null)} />}
      {labModal && <LabsMiniModal labGroup={labModal} onClose={() => setLabModal(null)} onSwitchToLabs={() => {}} />}
      {imagingModal && <ImagingModal eventData={imagingModal} patientId={patientId} data={data} onClose={() => setImagingModal(null)} />}
      {docModal && <DocumentModal docItem={docModal} patientId={patientId} data={data} onClose={() => setDocModal(null)} />}
    </div>
  );
};

export default CareIQView;
